import ConfirmModal from '@/components/ConfirmModal';
import { tenantSelector } from '@/features/configuration/configurationSelector';
import {
  deleteProfileLoadingSelector,
  profileConsentsLoadingSelector,
  saveProfileConsentsLoadingSelector,
} from '@/features/loading/loadingSelectors';
import {
  deleteProfile,
  getProfileConsents,
  saveProfileConsents,
} from '@/features/profile/profileActions';
import {
  profileConsentsSelector,
  profileDataSelector,
} from '@/features/profile/profileSelectors';
import { useAuth } from '@/hooks/useAuth';
import { TransAlert } from '@/i18n/trans/alert';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import type { ApiErrorResponse } from '@/types/api';
import { cn } from '@/utils/cn';
import { showErrorToast } from '@/utils/errorToast';
import { toConsentDecision } from '@/utils/profile';
import { sanitizeMarkup } from '@/utils/sanitizeMarkup';
import {
  Accordion,
  Button,
  Card,
  Checkbox,
  Divider,
  Icons,
  Input,
  Label,
  Spinner,
  Typography,
  typographyVariants,
} from '@packages/webshop-design-system';
import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

interface ConsentFormValues {
  consents: {
    code: string;
    // Customer-profile consents carry no mandatory flag today; keep it optional
    // so the UI can react if the API ever starts returning it.
    isMandatory?: boolean;
    description?: string;
    checked: boolean;
  }[];
}

const AccountDetailsSettings: FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const profile = useSelector(profileDataSelector);
  const profileConsents = useSelector(profileConsentsSelector);
  const isLoadingConsents = useSelector(profileConsentsLoadingSelector);
  const isSavingConsents = useSelector(saveProfileConsentsLoadingSelector);
  const isDeletingProfile = useSelector(deleteProfileLoadingSelector);
  const isMaas = useSelector(tenantSelector).value === 'maas';
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const profileId = profile?.id;

  useEffect(() => {
    if (profileId) {
      dispatch(getProfileConsents());
    }
  }, [dispatch, profileId]);

  const handleDeleteAccount = useCallback(async () => {
    try {
      await dispatch(deleteProfile()).unwrap();
      toast.success(<TransAlert i18nKey="accountSuccessfullyDeleted" />);
      await logout();
    } catch {
      toast.error(<TransAlert i18nKey="accountDeletionFailed" />);
    }
  }, [dispatch, logout]);

  const defaultValues = useMemo<ConsentFormValues>(
    () => ({
      consents:
        profileConsents?.map(({ code, consent, decision }) => ({
          // Retailer API types `code` as nullable, but consents always carry
          // one; fall back to '' to satisfy the form value type.
          code: code ?? '',
          description: consent,
          checked: decision?.id === 'CONSENT_DECISION.ACCEPTED',
        })) ?? [],
    }),
    [profileConsents]
  );

  const { control, handleSubmit, reset, formState } =
    useForm<ConsentFormValues>({ defaultValues });

  const { fields, update } = useFieldArray({
    control,
    name: 'consents',
  });

  // Profile consents load asynchronously, so sync the form once they arrive
  // (and again after a save refetch). This also rebaselines the dirty state.
  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const onSubmit = useCallback(
    async (data: ConsentFormValues) => {
      const consents = data.consents.map(toConsentDecision);

      try {
        await dispatch(saveProfileConsents(consents)).unwrap();
        toast.success(<TransText i18nKey="consentsSaved" />);
      } catch (e: unknown) {
        showErrorToast(
          <TransText i18nKey="savingConsentsFailed" />,
          e as ApiErrorResponse
        );
      }
    },
    [dispatch]
  );

  const handleRevert = useCallback(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  return (
    <div className="flex h-fit w-full flex-col gap-4 text-dark">
      {!isMaas && (
        <Card className="w-full rounded-lg px-6 pb-6 pt-4">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-3">
              <Icons.lock className="h-6 w-6" />
              <Typography variant="heading2" as="h2">
                <TransText i18nKey="loginDetails" />
              </Typography>
            </div>

            <Divider />

            <div className="flex flex-col gap-4">
              <Typography variant="body1-bold">
                <TransText i18nKey="logInWithEmailAndPassword" />
              </Typography>

              <div className="flex flex-col gap-1">
                <Label
                  htmlFor="account-email"
                  className={cn(
                    typographyVariants({ variant: 'body2' }),
                    'text-neutral'
                  )}
                >
                  <TransText i18nKey="email" />
                </Label>
                <Input
                  id="account-email"
                  data-testid="account-email-input"
                  placeholder={profile?.email ?? ''}
                  readOnly
                  disabled
                />
              </div>

              <Button
                variant="ghost"
                className="w-fit gap-2"
                startAdornment={<Icons.edit className="h-5 w-5" />}
                onClick={() => navigate('/account/change-password')}
                data-testid="change-password-button"
              >
                <TransText i18nKey="changePassword" />
              </Button>
            </div>
          </div>
        </Card>
      )}

      {!isMaas && (
        <Card>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
          >
            <div className="flex items-center gap-3">
              <Icons.userCheck className="h-6 w-6" />
              <Typography variant="heading2" as="h2">
                <TransText i18nKey="consents" />
              </Typography>
            </div>

            <Divider />

            <div className="flex flex-col gap-4" data-testid="account-consents">
              {isLoadingConsents && fields.length === 0 ? (
                <div className="flex justify-center py-4">
                  <Spinner size={24} />
                </div>
              ) : (
                fields.map((consent, index) => {
                  const markup = consent.description || '';

                  return (
                    <div key={consent.code} className="flex items-center gap-3">
                      <Checkbox
                        size="lg"
                        data-testid={`account-consent-checkbox-${index}`}
                        isSelected={consent.checked}
                        // Profile consents never come back mandatory today; the
                        // === true guard means everything stays editable while
                        // still honouring a future mandatory flag.
                        isDisabled={consent.isMandatory === true}
                        onChange={(checked) =>
                          update(index, { ...consent, checked })
                        }
                      />
                      <div className="flex flex-wrap items-center gap-0.5">
                        {markup ? (
                          <Typography
                            dangerouslySetInnerHTML={{
                              __html: sanitizeMarkup(markup),
                            }}
                            variant="body1"
                            className={cn('text-dark prose-a:text-blue-600')}
                          />
                        ) : (
                          <Typography variant="body1" className="text-dark">
                            <TransText i18nKey="undefinedConsentDescription" />
                          </Typography>
                        )}
                        {/* Only label as optional when the consent explicitly
                          reports isMandatory === false. A missing flag (the
                          current reality) renders no label at all. */}
                        {consent.isMandatory === false && (
                          <Typography variant="body2" className="text-neutral">
                            (<TransText i18nKey="optional" />)
                          </Typography>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="flex flex-wrap gap-4">
              <Button
                type="button"
                variant="tertiary"
                onClick={handleRevert}
                isDisabled={!formState.isDirty || isSavingConsents}
                data-testid="revert-consents-button"
              >
                <TransText i18nKey="revertChanges" />
              </Button>
              <Button
                type="submit"
                variant="secondary"
                className="gap-2"
                data-testid="save-consents-button"
                startAdornment={<Icons.check className="h-5 w-5" />}
                isPending={isSavingConsents}
                isDisabled={!formState.isDirty}
              >
                <TransText i18nKey="save" />
              </Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <Icons.infoCircle className="h-6 w-6" />
          <Typography variant="heading2" as="h2">
            <TransText i18nKey="deleteAccount" />
          </Typography>
        </div>

        <Divider />

        <Accordion
          classNames={{
            root: 'border-b-0',
            content: 'flex flex-col gap-4 pt-4',
          }}
          trigger={
            <Typography variant="body1-bold">
              <TransText i18nKey="permanentlyDeleteAccount" />
            </Typography>
          }
          data-testid="delete-account-accordion"
        >
          <Typography variant="body1" className="text-dark">
            <TransText i18nKey="deleteAccountDescription" />
          </Typography>

          <Button
            variant="destructive"
            className="w-fit gap-2"
            startAdornment={<Icons.trash className="h-5 w-5" />}
            onClick={() => setIsDeleteModalOpen(true)}
            data-testid="delete-account-button"
          >
            <TransText i18nKey="deleteAccount" />
          </Button>
        </Accordion>
      </Card>

      <ConfirmModal
        open={isDeleteModalOpen}
        setOpen={setIsDeleteModalOpen}
        title={
          <Typography variant="subtitle">
            <TransText i18nKey="permanentlyDeleteAccountConfirm" />
          </Typography>
        }
        description={
          <div className="flex flex-col gap-4">
            <Typography variant="body1">
              <TransText i18nKey="deleteAccountModalDescription" />
            </Typography>
            <Divider className="-mx-4 w-auto" />
          </div>
        }
        cancelLabel="cancel"
        confirmLabel="deleteAccount"
        confirmVariant="destructive"
        confirmStartAdornment={<Icons.trash className="h-5 w-5" />}
        isConfirmLoading={isDeletingProfile}
        onConfirm={handleDeleteAccount}
      />

      <Button
        variant="secondary"
        className="mt-4 w-fit gap-2"
        startAdornment={<Icons.logOut className="h-5 w-5" />}
        onClick={logout}
        data-testid="logout-button"
      >
        <TransText i18nKey="logOut" />
      </Button>
    </div>
  );
};

export default AccountDetailsSettings;
